import { View, Text, StyleSheet, Animated, Image } from 'react-native';

interface ProfileInfo {
  name: string;
  avatarUrl: string | null;
}

interface DuelScoreComparisonProps {
  challengerInfo: ProfileInfo;
  challengedInfo: ProfileInfo;
  challengerIsYou: boolean;
  challengedIsYou: boolean;
  challengerBest: number | null;
  challengedBest: number | null;
  challengerScoreStamp: Animated.Value;
  challengedScoreStamp: Animated.Value;
  opponentHasRecentActivity: boolean;
  isExpired: boolean;
  formatWeight: (weight: number, fromUnits?: string) => string;
  theme: any;
}

const DUEL_BLUE = '#3B82F6';
const DUEL_RED = '#EF4444';

export default function DuelScoreComparison({
  challengerInfo,
  challengedInfo,
  challengerIsYou,
  challengedIsYou,
  challengerBest,
  challengedBest,
  challengerScoreStamp,
  challengedScoreStamp,
  opponentHasRecentActivity,
  isExpired,
  formatWeight,
  theme,
}: DuelScoreComparisonProps) {
  const getScoreLabel = (best: number | null): string => {
    if (best == null) return isExpired ? 'No lift' : 'Waiting...';
    return String(formatWeight(best, 'lbs'));
  };

  const getScoreLabelColor = (best: number | null, isYou: boolean): string => {
    if (best == null) return isExpired ? '#ef4444' : theme.colors.textSecondary;
    return isYou ? DUEL_BLUE : DUEL_RED;
  };

  const renderAvatar = (info: ProfileInfo, isYou: boolean) => {
    const initial = (isYou ? 'Y' : info.name.charAt(0)).toUpperCase();
    const bgColor = isYou ? DUEL_BLUE : DUEL_RED;

    if (info.avatarUrl) {
      return (
        <Image
          source={{ uri: info.avatarUrl }}
          style={[styles.avatar, { backgroundColor: bgColor }]}
        />
      );
    }
    return (
      <View style={[styles.avatar, { backgroundColor: bgColor }]}>
        <Text style={[styles.avatarText, { color: theme.colors.accentForeground }]}>{initial}</Text>
      </View>
    );
  };

  return (
    <View style={styles.versus}>
      <View style={styles.athlete}>
        {renderAvatar(challengerInfo, challengerIsYou)}
        <Text style={styles.athleteName} numberOfLines={1}>
          {challengerInfo.name.toUpperCase()}
          {!challengerIsYou && opponentHasRecentActivity ? ' 🔥' : ''}
        </Text>
        <Animated.Text style={[
          styles.bestLift,
          {
            color: getScoreLabelColor(challengerBest, challengerIsYou),
            transform: [{ scale: challengerScoreStamp }],
          },
        ]}>
          {getScoreLabel(challengerBest)}
        </Animated.Text>
      </View>

      <View style={styles.vsBadge}>
        <Text style={styles.vsText}>VS</Text>
      </View>

      <View style={styles.athlete}>
        {renderAvatar(challengedInfo, challengedIsYou)}
        <Text style={styles.athleteName} numberOfLines={1}>
          {challengedInfo.name.toUpperCase()}
          {!challengedIsYou && opponentHasRecentActivity ? ' 🔥' : ''}
        </Text>
        <Animated.Text style={[
          styles.bestLift,
          {
            color: getScoreLabelColor(challengedBest, challengedIsYou),
            transform: [{ scale: challengedScoreStamp }],
          },
        ]}>
          {getScoreLabel(challengedBest)}
        </Animated.Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  versus: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 4,
  },
  athlete: {
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: '#2A2A2A',
  },
  avatarText: {
    fontSize: 22,
    fontFamily: 'Oswald_700Bold',
    color: '#E8E4DB',
  },
  athleteName: {
    fontSize: 12,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 1.5,
    color: '#E8E4DB',
    maxWidth: 100,
  },
  bestLift: {
    fontSize: 18,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 0.5,
  },
  vsBadge: {
    width: 36,
    height: 36,
    borderRadius: 2,
    backgroundColor: '#8B5CF6',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#6D28D9',
  },
  vsText: {
    fontSize: 13,
    fontFamily: 'Oswald_700Bold',
    color: '#fff',
    letterSpacing: 1,
  },
});
